"use client";
import styles from "./header.module.css";
import React, { useState } from "react";
import { MENU, HeaderMenuItem } from "./menuConfig";
import Link from "next/link";
import { useResponsive } from "@/hooks/useResponsive";
import { Menu as MenuIcon, X as CloseIcon } from "lucide-react";
import SearchOverlay from "./components/SearchOverlay";
import Language from "./components/Language";
import MobileMenuItem from "./components/MobileMenuItem";
import LanguageMobile from "./components/LanguageMobile";
import { dictionaries, Locale } from "@/utils/i18n";
import { useParams, usePathname } from "next/navigation";

export default function Header({ isTransparent }: { isTransparent?: boolean }) {
  const { isMobile, isDesktop } = useResponsive();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const params = useParams();
  const pathname = usePathname();
  const locale = (params?.locale as Locale) || "vi";
  const dict = dictionaries[locale] || dictionaries.vi;

  React.useEffect(() => {
    const handleScroll = () => {
      // Khi đang ở top trang (<= 20px) và chưa scroll thì là transparent,
      // Khi cuộn xuống (> 20px) thì hiện nền xanh
      setScrolled(window.scrollY > 20);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [pathname]);

  const isBgVisible = !isTransparent || scrolled;

  return (
    <header
      className={`${styles.header} ${isBgVisible ? styles.headerScrolled : ""} ${
        menuOpen ? styles.headerMenuOpen : ""
      }`}
    >
      {/* BACKGROUND SVG */}
      <div className={styles.svgContainer}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1920 160"
          preserveAspectRatio="xMidYMin slice"
          style={{ width: "100%", height: "100%", pointerEvents: "none" }}
        >
          <defs>
            <linearGradient
              id="headerGradient"
              x1="0.5"
              y1="90"
              x2="1923.5"
              y2="90"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="3.3%" stopColor="#0D5735" />
              <stop offset="51.9%" stopColor="#008748" />
              <stop offset="99.5%" stopColor="#0D5735" />
            </linearGradient>
            {/* Gradient RIÊNG cho STROKE mờ dần về 2 phía */}
            <linearGradient
              id="headerStrokeGradient"
              x1="0"
              y1="0"
              x2="1920"
              y2="0"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
              <stop offset="15%" stopColor="#ffffff" stopOpacity="0.6" />
              <stop offset="50%" stopColor="#ffffff" stopOpacity="1" />
              <stop offset="85%" stopColor="#ffffff" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* Lớp nền xanh có animation fade-in/out nhẹ nhàng qua opacity */}
          <path
            d="M1922 -1V124.842C1922 124.842 1127.76 125 1087.7 125C1047.64 125 1050.64 131 1027.6 152.5C1004.57 174 974.522 178 959.999 178C945.477 178 915.43 174 892.394 152.5C869.358 131 872.362 125 833.302 125C794.241 125 -1 124.842 -1 124.842V-1H1922Z"
            fill="url(#headerGradient)"
            transform="scale(1, 0.63)"
            style={{
              opacity: isBgVisible ? 1 : 0,
              transition: "opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          />
          {/* Lớp viền stroke luôn sắc nét */}
          <path
            d="M1922 -1V124.842C1922 124.842 1127.76 125 1087.7 125C1047.64 125 1050.64 131 1027.6 152.5C1004.57 174 974.522 178 959.999 178C945.477 178 915.43 174 892.394 152.5C869.358 131 872.362 125 833.302 125C794.241 125 -1 124.842 -1 124.842V-1H1922Z"
            fill="none"
            stroke="url(#headerStrokeGradient)"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
            transform="scale(1, 0.63)"
          />
        </svg>
      </div>
      {/* INNER CONTENT */}
      <div className={styles.inner}>
        <div
          className={`${styles.leftMenu} ${isMobile ? styles.leftMenuMobile : ""
            }`}
        >
          {!isMobile &&
            MENU.slice(0, 3).map((item) => (
              <HeaderMenuItem key={item.href} item={item} trans={dict} locale={locale} />
            ))}
          {isMobile && (
            <button
              className={styles.menuToggle}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {!menuOpen && <MenuIcon size={24} />}
            </button>
          )}
        </div>

        <div className={styles.logoWrapper}>
          <Link href={`/${locale}`}>
            <img
              src="/logos/logo_white_v2.png"
              alt="Logos"
              className={styles.logo}
            />
          </Link>
        </div>

        <div className={styles.rightMenu}>
          {!isMobile ? (
            <div className={styles.rightMenu}>
              {MENU.slice(3, 6).map((item) => (
                <HeaderMenuItem key={item.href} item={item} trans={dict} locale={locale} />
              ))}

              <div className={styles.rightSide}>
                <Language />
                <SearchOverlay />
              </div>
            </div>
          ) : (
            <div className={styles.rightMobileMenu}>
              <SearchOverlay />
            </div>
          )}
        </div>
      </div>

      {/* MOBILE MENU */}
      {isMobile && menuOpen && (
        <>
          <div className={styles.backdrop} onClick={() => setMenuOpen(false)} />
          <div className={`${styles.mobileMenu} ${styles.open}`}>
            <button
              className={styles.closeBtn}
              onClick={() => setMenuOpen(false)}
            >
              <CloseIcon size={26} />
            </button>

            <div className={styles.mobileMenuContent}>
              {MENU.map((item) => (
                <MobileMenuItem 
                  key={item.href} 
                  item={item} 
                  trans={dict} 
                  locale={locale}
                  onClose={() => setMenuOpen(false)} 
                />
              ))}

              <Link 
                href={`/${locale}/san-pham`} 
                className={styles.mobileBuyBtn}
                onClick={() => setMenuOpen(false)}
              >
                {dict.menu.muahang.title}
              </Link>
              <LanguageMobile onClose={() => setMenuOpen(false)} />
            </div>
          </div>
        </>
      )}
    </header>
  );
}
